import React, {
  useCallback, useEffect, useState
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import shortid from 'shortid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarMinus, faCalendarPlus, faSearch, faSlidersH, faSpinner,
  faTimes, faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';
import {
  Chip, Drawer, Grid, IconButton,
  InputAdornment, TextField
} from '@material-ui/core';
import { translate } from 'utils/translation';
import { useDebounce } from 'utils/helpers';
import { InfoTooltip } from '..';
import { Button } from '../Button';

const InputAndFilters = styled(Grid).attrs({
  alignItems: 'center',
  justifyContent: 'space-between'
})`
  padding: 1rem 0;
  margin-bottom: 1rem;
  border-radius: var(--borderRadius);

  .MuiTextField-root {
    flex: 1;
  }

  input {
    &:disabled {
      cursor: not-allowed;
    }
  }

  & > button {
    margin-left: 1rem;

    &:disabled {
      cursor: not-allowed;
      pointer-events: all;
    }
  }

  @media (max-width: 768px) {
    & > button {
      margin-left: 0;
    }
  }
`;

const TagList = styled(Grid)`
  flex: 1;
  overflow-x: auto;

  div:not(:last-child),
  svg:not(.MuiSvgIcon-root) {
    margin-right: 0.5rem;
  }
`;

const FilterDrawerMain = styled.main`
  width: 100%;
  min-width: var(--wrapperWidthMin);
  flex: 1;
  margin: 0 auto;
  padding: 0 2rem;

  h2 {
    justify-content: flex-start;
  }
`;

const FilterDrawerButtons = styled(Grid).attrs({
  alignItems: 'center',
  justifyContent: 'flex-end'
})`
  padding: 2rem;

  @media (min-width: 481px) {
    button:first-child {
      margin-right: 2rem;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;

    button:first-child {
      margin-bottom: 2rem;
    }
  }
`;

export const GenericFilters = ({
  ComponentFilter,
  dataTour,
  filters, setFilters,
  search, setSearch,
  setCurrentPage,
  filterKey,
  withDrawer,
  disabled,
  tooltip
}) => {
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(filters);
  const [internalSearch, setInternalSearch] = useState(search);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedSearch = useDebounce(internalSearch, 1000);

  useEffect(() => {
    setSearch(debouncedSearch);
    setIsSearching(false);
  }, [setSearch, debouncedSearch]);

  const handleInputChange = useCallback((input) => {
    setIsSearching(true);
    setInternalSearch(input.target.value);
  }, []);

  const saveFilters = useCallback(() => {
    localStorage.setItem(`filters_${filterKey}`, JSON.stringify(currentFilters));
  }, [filterKey, currentFilters]);

  const removeFilters = useCallback(() => {
    setFilters([]);
    setCurrentFilters([]);
    localStorage.removeItem(`filters_${filterKey}`);
  }, [setFilters, filterKey]);

  const resetFilters = useCallback(() => {
    setFilters(filters);
    setCurrentFilters(filters);
    setIsFiltersDrawerOpen(false);
  }, [filters, setFilters]);

  const setNewFilters = useCallback(() => {
    setFilters(currentFilters);
    setIsFiltersDrawerOpen(false);
    setCurrentPage && setCurrentPage(0);
    saveFilters();
  }, [currentFilters, setFilters, saveFilters, setCurrentPage]);

  const handleDeleteSearch = useCallback(() => setInternalSearch(''), []);

  const handleOpenDrawer = useCallback(() => setIsFiltersDrawerOpen(true), []);

  const handleCloseDrawer = useCallback(() => setIsFiltersDrawerOpen(false), []);

  return (
    <>
      <aside data-tour={dataTour}>
        <InputAndFilters container>
          {tooltip && (
            <InfoTooltip label={translate(tooltip)} />
          )}
          <TextField
            autoComplete="off"
            disabled={disabled}
            InputProps={{
              endAdornment: internalSearch !== '' && (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={handleDeleteSearch}>
                    <FontAwesomeIcon color="var(--grey)" icon={faTimes} size="xs" />
                  </IconButton>
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching
                    ? <FontAwesomeIcon icon={faSpinner} spin />
                    : <FontAwesomeIcon color="var(--grey)" icon={faSearch} />}
                </InputAdornment>
              )
            }}
            label={translate('common.search')}
            name="search"
            value={internalSearch}
            variant="outlined"
            onChange={handleInputChange}
          />
          {withDrawer && (
            <Button
              data-cy="genericListFilters"
              disabled={disabled}
              startIcon={<FontAwesomeIcon icon={faSlidersH} />}
              type="secondary"
              onClick={handleOpenDrawer}
            >
              {translate('button.filters')}
            </Button>
          )}
        </InputAndFilters>
        {filters && filters.length > 0 && (
          <Grid alignItems="center" container justifyContent="space-between" style={{ marginBottom: '2rem' }}>
            <TagList data-cy="genericListFiltersList">
              {filters.map((filter) => {
                if (!filter.label) return false;
                let formattedFilterLabel;
                if (typeof filter.label === 'object' || filter.label.endsWith('Z')) {
                  formattedFilterLabel = (
                    <>
                      <FontAwesomeIcon icon={filter.key === 'startDate' ? faCalendarMinus : faCalendarPlus} />
                      {new Date(filter.label).toLocaleDateString()}
                    </>
                  );
                } else if (filter.value && filter.value.label) {
                  formattedFilterLabel = filter.value.label;
                } else {
                  formattedFilterLabel = filter.label;
                }

                if (filter.translationKey) {
                  formattedFilterLabel = translate(`${filter.translationKey}.${formattedFilterLabel}`);
                }

                return (
                  <Chip
                    color="primary"
                    key={shortid.generate()}
                    label={formattedFilterLabel}
                    onDelete={() => {
                      setFilters(filters.filter((f) => f.key !== filter.key));
                      setCurrentFilters(filters.filter((f) => f.key !== filter.key));
                    }}
                  />
                );
              })}
            </TagList>
            {filters.length > 0 && (
              <Button
                startIcon={<FontAwesomeIcon icon={faTrashAlt} />}
                type="danger"
                variant="text"
                onClick={removeFilters}
              >
                {translate('button.removeFilters')}
              </Button>
            )}
          </Grid>

        )}
      </aside>

      {withDrawer && (
        <Drawer anchor="right" id="filterDrawer" open={isFiltersDrawerOpen} onClose={handleCloseDrawer}>
          <Grid container justifyContent="flex-end">
            <Button variant="text" onClick={resetFilters}>
              <FontAwesomeIcon color="var(--blue)" icon={faTimes} size="2x" />
            </Button>
          </Grid>
          <FilterDrawerMain>
            {ComponentFilter && ComponentFilter({ currentFilters, setCurrentFilters })}
          </FilterDrawerMain>
          <FilterDrawerButtons container>
            <Button data-cy="genericListFilterCancel" type="secondary" onClick={resetFilters}>
              {translate('button.cancel')}
            </Button>
            <Button data-cy="genericListFilterValidate" onClick={setNewFilters}>
              {translate('button.validateFilters')}
            </Button>
          </FilterDrawerButtons>
        </Drawer>
      )}
    </>
  );
};

GenericFilters.propTypes = {
  ComponentFilter: PropTypes.func,
  dataTour: PropTypes.string,
  filterKey: PropTypes.string,
  filters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ])
  })),
  search: PropTypes.string,
  setFilters: PropTypes.func,
  setSearch: PropTypes.func,
  setCurrentPage: PropTypes.func,
  tooltip: PropTypes.string,
  withDrawer: PropTypes.bool
};

GenericFilters.defaultProps = {
  ComponentFilter: null,
  dataTour: '',
  filterKey: 'generic_',
  filters: [],
  search: '',
  setCurrentPage: null,
  setFilters: null,
  setSearch: null,
  tooltip: '',
  withDrawer: false
};